import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Build Your Box (sections/gw-build-box.liquid).
 *
 * One grid of products, each with "Add to Box". Every product has a type (effervescent / tablets) with a
 * set number of slots; picks can repeat. Once every slot is filled, the fixed-price box product is added
 * as one cart line with the picks as line item properties (e.g. "Effervescent: Nap Easy × 2, Liver Detox"),
 * then the same CartLinesUpdateEvent as the product form is dispatched so the cart drawer and icon update.
 */

const SOURCE = 'gw-build-box';

/** Section ids of every cart-items-component on the page, so their markup comes back with the add. */
const sectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean)
    .join(',');

const escapeHtml = (text) => text.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

class GwBuildBox extends HTMLElement {
  connectedCallback() {
    /** @type {{ group: string, id: string, title: string, img: string, price: number }[]} */
    this.picks = [];
    this.strings = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');
    this.cards = [...this.querySelectorAll('[data-item]')];

    /** @type {Map<string, { need: number, unit: string }>} type key → slots and display name */
    this.groups = new Map();
    for (const card of this.cards) {
      if (this.groups.has(card.dataset.group)) continue;
      this.groups.set(card.dataset.group, {
        need: Number(card.dataset.need),
        unit: card.querySelector('.gw-bbx__type')?.textContent.trim() || card.dataset.group,
      });
    }

    this.cta = this.querySelector('[data-cta]');
    this.error = this.querySelector('[data-error]');
    this.abort = new AbortController();
    this.addEventListener('click', (event) => this.onClick(event), { signal: this.abort.signal });
    this.render();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  countIn(key) {
    return this.picks.filter((p) => p.group === key).length;
  }

  onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const filter = target.closest('[data-filter]');
    if (filter) return this.filter(filter.dataset.filter);

    const slotRemove = target.closest('[data-remove-slot]');
    if (slotRemove) {
      this.picks.splice(Number(slotRemove.dataset.removeSlot), 1);
      return this.render();
    }

    if (target.closest('[data-cta]')) return this.addToCart();

    const card = target.closest('[data-item]');
    if (!card) return;
    const key = card.dataset.group;

    if (target.closest('[data-add], [data-plus]')) {
      if (this.countIn(key) >= this.groups.get(key).need) return;
      const { id, title, img, price } = card.dataset;
      this.picks.push({ group: key, id, title, img, price: Number(price) });
      this.render();
    } else if (target.closest('[data-minus]')) {
      const index = this.picks.findLastIndex((p) => p.id === card.dataset.id);
      if (index > -1) this.picks.splice(index, 1);
      this.render();
    }
  }

  /** Show all products or one type. */
  filter(key) {
    for (const pill of this.querySelectorAll('[data-filter]')) {
      const active = pill.dataset.filter === key;
      pill.classList.toggle('is-active', active);
      pill.setAttribute('aria-pressed', String(active));
    }
    for (const card of this.cards) card.hidden = key !== 'all' && card.dataset.group !== key;
  }

  money(cents) {
    const amount = (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: cents % 100 ? 2 : 0 });
    return (this.dataset.moneyFormat || '₹{{amount}}').replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  render() {
    const total = [...this.groups.values()].reduce((sum, g) => sum + g.need, 0);
    const filled = this.picks.length;

    for (const [key, g] of this.groups) {
      const chip = this.querySelector(`[data-chip="${key}"]`);
      if (!chip) continue;
      const count = this.countIn(key);
      chip.classList.toggle('is-full', count >= g.need);
      chip.querySelector('[data-count]').textContent = `${count}/${g.need}`;
    }

    for (const card of this.cards) {
      const full = this.countIn(card.dataset.group) >= this.groups.get(card.dataset.group).need;
      const qty = this.picks.filter((p) => p.id === card.dataset.id).length;
      card.classList.toggle('is-picked', qty > 0);
      const badge = card.querySelector('[data-badge]');
      badge.hidden = qty === 0;
      badge.textContent = `×${qty}`;
      const add = card.querySelector('[data-add]');
      if (!add) continue;
      add.hidden = qty > 0;
      add.disabled = full;
      add.textContent = full ? add.dataset.full : add.dataset.label;
      card.querySelector('[data-stepper]').hidden = qty === 0;
      card.querySelector('[data-qty]').textContent = qty;
      card.querySelector('[data-plus]').disabled = full;
    }

    // Slots (hero and tray): each type's picks fill that type's slots in the order they were picked.
    for (const list of this.querySelectorAll('[data-slots]')) {
      const used = {};
      for (const slot of list.querySelectorAll('[data-slot]')) {
        const key = slot.dataset.slot;
        const own = this.picks.map((p, i) => ({ ...p, i })).filter((p) => p.group === key);
        used[key] = (used[key] ?? -1) + 1;
        const pick = own[used[key]];
        slot.classList.toggle('is-filled', Boolean(pick));
        const title = pick && escapeHtml(pick.title);
        slot.innerHTML = pick
          ? `<img src="${pick.img}" alt="${title}" width="80" height="80"><button type="button" data-remove-slot="${pick.i}" aria-label="Remove ${title}">×</button>`
          : `<span>${escapeHtml(this.groups.get(key)?.unit ?? '')}</span>`;
      }
    }

    this.querySelector('[data-progress]').textContent = `${filled}/${total}`;

    // Once the box is full, show what the picks cost on their own and the saving.
    const price = Number(this.dataset.price);
    const value = this.picks.reduce((sum, p) => sum + p.price, 0);
    const valueEl = this.querySelector('[data-value]');
    const saveEl = this.querySelector('[data-save]');
    const complete = filled === total;
    valueEl.hidden = saveEl.hidden = !complete || value <= price;
    valueEl.textContent = this.money(value);
    saveEl.textContent = (this.strings.save || '').replace('[amount]', this.money(value - price));

    this.cta.disabled = !complete;
    this.cta.textContent = complete ? this.cta.dataset.label : (this.strings.pickMore || '').replace('[count]', total - filled);
    this.error.hidden = true;
  }

  /** "Nap Easy × 2, Liver Detox" for one type. */
  describe(key) {
    const counts = new Map();
    for (const p of this.picks) if (p.group === key) counts.set(p.title, (counts.get(p.title) || 0) + 1);
    return [...counts].map(([title, n]) => (n > 1 ? `${title} × ${n}` : title)).join(', ');
  }

  async addToCart() {
    if (this.cta.disabled || this.cta.hasAttribute('aria-busy')) return;
    this.cta.setAttribute('aria-busy', 'true');

    const properties = {};
    for (const [key, g] of this.groups) properties[g.unit] = this.describe(key);
    properties._box_items = this.picks.map((p) => p.id).join(',');

    const variantId = this.dataset.variant;
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties }], sections: sectionIds() }),
      }).then((res) => res.json());

      const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
      const didError = Boolean(response.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { source: SOURCE, items: cart.items, sections: response.sections, didError },
      });
      if (didError) throw new Error(response.description || response.message);

      this.picks = [];
      this.render();
      this.cta.textContent = this.cta.dataset.added;
      // Open the cart drawer even with the theme's auto-open setting off, so the box is seen going in.
      const drawer = document.getElementById('cart-drawer');
      if (drawer && 'open' in drawer && !drawer.hasAttribute('open')) requestAnimationFrame(() => drawer.open());
    } catch (error) {
      console.error(error);
      deferred.reject?.(error);
      this.error.textContent = this.strings.error;
      this.error.hidden = false;
    } finally {
      this.cta.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('gw-build-box')) customElements.define('gw-build-box', GwBuildBox);
